// Lightweight i18n — no extra deps. Loads the active locale's JSON
// catalog + exposes useTranslation(). Per LS-SPEC-32, Triton output
// honours keigo (politeness) levels — see keigo.ts.

import { useEffect, useState } from "react";

import enCommon from "../../locales/en/common.json";
import jaCommon from "../../locales/ja/common.json";


export type Locale = "en" | "ja";


const CATALOGS: Record<Locale, Record<string, string>> = {
  en: enCommon,
  ja: jaCommon,
};


const LOCALE_STORAGE_KEY = "lifestyle.locale.v1";


export function detectLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored === "en" || stored === "ja") return stored;
  // Honour browser locale on first visit.
  const browser = (navigator.language || "en").toLowerCase();
  return browser.startsWith("ja") ? "ja" : "en";
}


export function setLocale(locale: Locale): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    window.dispatchEvent(new CustomEvent("lifestyle.locale-change"));
  } catch {
    // SSR / storage quota
  }
}


/** Synchronous lookup. Falls back to the key itself when missing. */
export function t(key: string, locale?: Locale): string {
  const loc = locale ?? detectLocale();
  return CATALOGS[loc][key] ?? key;
}


/**
 * React hook that re-renders on locale change.
 * Returns ``{ locale, t }`` where ``t(key)`` resolves to the active
 * locale's string (or the key itself on miss).
 */
export function useTranslation() {
  const [locale, set] = useState<Locale>(detectLocale);

  useEffect(() => {
    const handler = () => set(detectLocale());
    window.addEventListener("lifestyle.locale-change", handler);
    return () => window.removeEventListener("lifestyle.locale-change", handler);
  }, []);

  return {
    locale,
    t: (key: string) => t(key, locale),
    setLocale: (l: Locale) => { setLocale(l); set(l); },
  };
}
